/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AH5EventReportor
 */

import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { IEventReportor } from 'com/didichuxing/tracker/plugin/reportor/IEventReportor';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import GlobalUtil from 'com/didichuxing/tracker/plugin/utils/GlobalUtil';
import DataQueueSet from 'com/didichuxing/tracker/plugin/reportor/DataQueueSet';

interface IUnreportedItem {

    /**
     * 未上报事件
     */
    event: ITrackedEvent;

    /**
     * 该事件上报成功后执行的方法。
     * @param {ITrackedEvent} evt
     */
    resolve(evt: ITrackedEvent): void;

    /**
     * 该事件上报失败后执行的方法。
     * @param {ITrackedEvent} evt
     */
    reject(evt: ITrackedEvent): void;
}

export default abstract class AH5EventReportor implements IEventReportor {

    private context: ITrackerContext;
    private event: ITrackedEvent;

    /**
     * 待上报事件。
     */
    private unreportedEvents: IUnreportedItem[];
    /**
     * 上报失败事件
     */
    protected readonly errorreportedEvents = DataQueueSet.getInstance();

    /**
     * @constructor
     * @param {ITrackerContext} context
     * @param {ITrackedEvent} event
     */
    constructor(context: ITrackerContext, event: ITrackedEvent) {
        this.event = event;
        this.context = context;
        this.unreportedEvents = [];
        this.reportEventsAfterLoad = this.reportEventsAfterLoad.bind(this);
        window.addEventListener('load', this.reportEventsAfterLoad);
    }

    /**
     * load之后，将未上报事件集中依次上报。
     */
    private reportEventsAfterLoad(): void {
        window.removeEventListener('load', this.reportEventsAfterLoad);
        while (this.unreportedEvents.length) {
            const unreportedItem: IUnreportedItem | undefined = this.unreportedEvents.shift();
            if (unreportedItem) {
                this.reportEventImmediately(unreportedItem.event).then(unreportedItem.resolve, unreportedItem.reject);
            }
        }
    }

    /**
     * 得到context信息
     * @returns {ITrackerContext}
     */
    protected getContext(): ITrackerContext {
        return this.context;
    }

    /**
     * 上报路径path
     * @returns {string}
     */
    protected get reportPath(): string {
        return '/api/web/stat';
    }

    /**
     * @override
     * @inheritDoc
     * @returns {Promise<ITrackedEvent>}
     */
    public reportEvent(): Promise<ITrackedEvent> {
        if (!GlobalUtil.isEmptyString(this.event.getData().appKey)) {
            GlobalUtil.dispatchOmgLog('[Omega] H5 appKey 参数 不可为空');
            throw new Error('[Omega] H5 appKey 参数 不可为空');
            return Promise.reject();
        }

        if (GlobalUtil.isLoaded() || this.event.getType() === PluginUtils.EVENT_ID_PAGE_INIT ||
            this.event.getType() === PluginUtils.EVENT_ID_OMG_LOG) {
            // onload已经执行完或者当前事件为页面初始化事件
            // 如果有上报错误的数据也去上报
            if (!this.errorreportedEvents.empty()) {
                this.errorreportedEvents.getData().forEach((value) => {
                    this.reportEventImmediately(value);
                });
            }
            this.errorreportedEvents.removeAll();
            return this.reportEventImmediately(this.event);
        } else {
            // load未完成，将事件聚集，等待集中上报。
            return new Promise<ITrackedEvent>((resolve: (evt: ITrackedEvent) => void,
                                               reject: (evt: ITrackedEvent) => void): void => {
                this.unreportedEvents.push({
                    event: this.event,
                    resolve: resolve,
                    reject: reject
                });
            });
        }
    }

    /**
     * 马上直接上报事件。
     * @param {ITrackedEvent} event
     * @returns {Promise<ITrackedEvent>}
     */
    protected abstract reportEventImmediately(event: ITrackedEvent): Promise<ITrackedEvent>;
}
